/* Simulateur de l'app (console › Réglages › Application) — chargé AVANT tout le reste.

   L'école (20.10.2026) : « je veux cliquer dans l'application et voir ce qu'ils voient,
   sans me connecter sur mon téléphone avec les comptes de test ». La console ouvre la
   vraie app (/espace, /prof, /direction) dans un téléphone dessiné, avec un jeton de
   SIMULATION qu'elle pose sur sa propre fenêtre (window.__simulationApp).

   Ici, et seulement dans un cadre de la console (même site), l'app :
   - range ses clés dans une mémoire à elle : la vraie session de la console (admin_token),
     d'une famille ou d'un prof dans ce navigateur n'est ni lue, ni écrasée, ni effacée ;
   - ajoute l'en-tête X-Simulation à chaque appel au serveur, sans cookie : le serveur
     identifie la personne simulée et REFUSE toute écriture (console/simulation.py) ;
   - bloque d'elle-même toute écriture vers un autre site.
   Hors de ce cadre, ce fichier ne fait rien. */

const SERVEUR = process.env.REACT_APP_BACKEND_URL || "";
const ENTETE = "X-Simulation";
export const REFUS = "Simulation : lecture seule — rien n'est enregistré ni envoyé.";
// Des POST qui ne font que lire (le mode école demande ainsi l'état des notifications).
const LECTURES_EN_POST = ["/api/console/mobile/push/etat"];
// La clé où chaque app cherche sa session : l'app se croit connectée comme la personne.
const CLES = { famille: "espace_session", prof: "prof_session", ecole: "admin_token" };

function lireLaConsole() {
  try {
    if (typeof window === "undefined" || window.self === window.top) return null;
    // Un parent d'un autre site lève une erreur ici : pas de simulation possible.
    const s = window.parent.__simulationApp;
    return s && s.jeton && CLES[s.profil] ? s : null;
  } catch {
    return null;
  }
}

const sim = lireLaConsole();

export const enSimulation = () => !!sim;

function memoire() {
  const m = new Map();
  return {
    getItem: (k) => (m.has(String(k)) ? m.get(String(k)) : null),
    setItem: (k, v) => { m.set(String(k), String(v)); },
    removeItem: (k) => { m.delete(String(k)); },
    clear: () => m.clear(),
    key: (i) => [...m.keys()][i] ?? null,
    get length() { return m.size; },
  };
}

function adresse(url) {
  try { return new URL(String(url), window.location.href); } catch { return null; }
}

const origineServeur = adresse(SERVEUR)?.origin || "";
const versLeServeur = (url) => !!origineServeur && adresse(url)?.origin === origineServeur;
const lecture = (methode, url) => ["GET", "HEAD", "OPTIONS"].includes(String(methode || "GET").toUpperCase())
  || LECTURES_EN_POST.includes(adresse(url)?.pathname || "");

if (sim) {
  const local = memoire();
  local.setItem(CLES[sim.profil], sim.jeton);
  Object.defineProperty(window, "localStorage", { value: local, configurable: true });
  Object.defineProperty(window, "sessionStorage", { value: memoire(), configurable: true });

  const fetchDOrigine = window.fetch.bind(window);
  window.fetch = (entree, options = {}) => {
    const requete = typeof entree === "string" || entree instanceof URL ? null : entree;
    const url = requete ? requete.url : String(entree);
    const methode = options.method || requete?.method || "GET";
    if (versLeServeur(url)) {
      const entetes = new Headers(options.headers || requete?.headers || {});
      entetes.set(ENTETE, sim.jeton);
      return fetchDOrigine(entree, { ...options, headers: entetes, credentials: "omit" });
    }
    if (!lecture(methode, url)) {
      return Promise.resolve(new Response(JSON.stringify({ detail: REFUS }),
        { status: 403, headers: { "Content-Type": "application/json" } }));
    }
    return fetchDOrigine(entree, options);
  };

  // axios passe par XMLHttpRequest.
  const ouvrir = XMLHttpRequest.prototype.open;
  const envoyer = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function open(methode, url, ...reste) {
    this.__simulation = { methode, url };
    return ouvrir.call(this, methode, url, ...reste);
  };
  XMLHttpRequest.prototype.send = function send(corps) {
    const { methode, url } = this.__simulation || {};
    if (versLeServeur(url)) {
      this.withCredentials = false;
      this.setRequestHeader(ENTETE, sim.jeton);
    } else if (!lecture(methode, url)) {
      // Une écriture vers un autre site : on la renvoie au serveur de l'école, qui la
      // refuse avec le message de la simulation (aucune n'existe aujourd'hui).
      ouvrir.call(this, "POST", `${SERVEUR}/api/simulation-refusee`);
      this.setRequestHeader(ENTETE, sim.jeton);
    }
    return envoyer.call(this, corps);
  };

  if (navigator.sendBeacon) navigator.sendBeacon = () => true;

  // La personne a l'app installée : l'app se croit ouverte depuis son icône, comme sur son
  // téléphone (pas d'« Installer l'application » ni de « Retour au site »).
  if (sim.installee) {
    const matchMediaDOrigine = window.matchMedia.bind(window);
    window.matchMedia = (requete) => (/display-mode:\s*standalone/.test(String(requete))
      ? { matches: true, media: String(requete), onchange: null, addListener() {}, removeListener() {},
        addEventListener() {}, removeEventListener() {}, dispatchEvent: () => false }
      : matchMediaDOrigine(requete));
    try { Object.defineProperty(window.navigator, "standalone", { value: true, configurable: true }); } catch { /* rien */ }
  }
}
